import React from 'react';

function EnhancedLoshuGrid({ gridData, birthNumbers }) {
  if (!gridData || !gridData.number_sources) return null;

  const { number_sources, derived_numbers, enhanced_yogas } = gridData;

  // Grid positions (standard Lo Shu layout)
  const gridLayout = [
    [4, 9, 2],
    [3, 5, 7],
    [8, 1, 6]
  ];

  const getNumberColor = (num, source) => {
    if (source.from_kua > 0) return '#3B82F6'; // Blue for KUA
    if (source.from_conductor > 0) return '#A855F7'; // Purple for Conductor
    if (source.from_driver > 0) return '#F59E0B'; // Orange for Driver
    if (source.native_count > 0) return '#10B981'; // Green for native
    return '#4B5563'; // Grey for missing
  };

  const renderNumber = (num) => {
    const source = number_sources[num];
    if (!source) return null;

    const { native_count, from_kua, from_conductor, from_driver, total_count } = source;
    const missing = total_count === 0;

    // Create array of actual digits to display
    const displayNumbers = [];
    
    // Add native numbers (green)
    for (let i = 0; i < native_count; i++) {
      displayNumbers.push({ value: num, color: '#10B981', source: 'native' });
    }
    
    // Add KUA (blue)
    if (from_kua > 0) {
      displayNumbers.push({ value: num, color: '#3B82F6', source: 'KUA' });
    }
    
    // Add Conductor (purple)
    if (from_conductor > 0) {
      displayNumbers.push({ value: num, color: '#A855F7', source: 'Conductor' });
    }
    
    // Add Driver (orange)
    if (from_driver > 0) {
      displayNumbers.push({ value: num, color: '#F59E0B', source: 'Driver' });
    }

    return (
      <div style={{
        aspectRatio: '1',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: missing ? '#1A1B2E' : 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.05) 100%)',
        border: missing ? '2px dashed #374151' : '3px solid #10B981',
        borderRadius: '16px',
        padding: '0.5rem',
        position: 'relative',
        minHeight: '100px'
      }}>
        {/* Main number */}
        <div style={{ fontSize: '2rem', fontWeight: 700, color: missing ? '#4B5563' : '#10B981', marginBottom: '0.5rem' }}>
          {num}
        </div>

        {/* Show actual numbers */}
        {!missing && (
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '4px', 
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            {displayNumbers.map((digit, idx) => (
              <div
                key={idx}
                style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '4px',
                  background: digit.color,
                  color: '#FFF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
                title={digit.source}
              >
                {digit.value}
              </div>
            ))}
          </div>
        )}

        {missing && (
          <div style={{ fontSize: '0.625rem', color: '#6B7280' }}>Missing</div>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(3, 1fr)', 
        gap: '12px', 
        maxWidth: '450px', 
        margin: '0 auto 2rem'
      }}>
        {gridLayout.flat().map((num) => (
          <div key={num}>
            {renderNumber(num)}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div style={{ 
        display: 'flex', 
        gap: '1.5rem', 
        flexWrap: 'wrap', 
        justifyContent: 'center',
        marginBottom: '2rem',
        padding: '1rem',
        background: '#1E2139',
        borderRadius: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ width: '16px', height: '16px', borderRadius: '4px', background: '#10B981' }} />
          <span style={{ fontSize: '0.875rem', color: '#D1D5DB' }}>Native (from DOB)</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ width: '16px', height: '16px', borderRadius: '4px', background: '#F59E0B' }} />
          <span style={{ fontSize: '0.875rem', color: '#D1D5DB' }}>Driver ({derived_numbers?.driver})</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ width: '16px', height: '16px', borderRadius: '4px', background: '#A855F7' }} />
          <span style={{ fontSize: '0.875rem', color: '#D1D5DB' }}>Conductor ({derived_numbers?.conductor})</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ width: '16px', height: '16px', borderRadius: '4px', background: '#3B82F6' }} />
          <span style={{ fontSize: '0.875rem', color: '#D1D5DB' }}>KUA ({derived_numbers?.kua})</span>
        </div>
      </div>

      {/* Enhanced Yogas */}
      {enhanced_yogas && enhanced_yogas.length > 0 && (
        <div style={{ marginTop: '2rem' }}>
          <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#F59E0B' }}>
            ⭐ Yogas Activated by Derived Numbers!
          </h3>
          {enhanced_yogas.map((yoga, i) => (
            <div key={i} style={{ 
              padding: '1.5rem', 
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(245, 158, 11, 0.05) 100%)',
              borderRadius: '12px',
              border: '2px solid #F59E0B',
              marginBottom: '1rem'
            }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem', color: '#F59E0B' }}>
                {yoga.name}
              </h4>
              <p style={{ fontSize: '0.875rem', color: '#9CA3AF', marginBottom: '0.75rem' }}>
                Completed by: {yoga.completed_by.join(', ')}
              </p>
              <p style={{ fontSize: '1rem', lineHeight: 1.7, color: '#E5E7EB' }}>
                <strong style={{ color: '#F59E0B' }}>Special Benefit:</strong> {yoga.benefit}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default EnhancedLoshuGrid;
